// Wrapper-side SW port handoff and respawn recovery. Hands the SW
// a MessageChannel port on boot, then listens for `p2claw_sw_wake`
// + `controllerchange` to re-hand on respawn / bundle update.

import {
  SW_MSG_VERSION,
  type SwMessage,
} from "../sw/messaging.js";
import { debugLog } from "../debug-log.js";
import type { TranslatorClientLike } from "./session.js";

/** Subset of `ServiceWorkerContainer` the handoff drives; tests
 *  substitute a fake without a real browser. */
export interface SwContainerLike {
  readonly controller: SwControllerLike | null;
  addEventListener(
    type: "controllerchange",
    listener: () => void,
  ): void;
  removeEventListener(
    type: "controllerchange",
    listener: () => void,
  ): void;
  addEventListener(
    type: "message",
    listener: (ev: { data: unknown }) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (ev: { data: unknown }) => void,
  ): void;
}

export interface SwControllerLike {
  postMessage(msg: unknown, transfer?: Transferable[]): void;
}

export type MessageChannelFactory = () => {
  port1: MessagePortLike;
  port2: MessagePortLike;
};

export interface MessagePortLike {
  postMessage(msg: unknown, transfer?: Transferable[]): void;
  addEventListener(
    type: "message",
    listener: (ev: { data: unknown }) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (ev: { data: unknown }) => void,
  ): void;
  close?: () => void;
  start?: () => void;
}

export interface InstallSwHandoffOptions {
  navigatorSw: SwContainerLike;
  translator: TranslatorClientLike;
  /** Pristine wrapper HTML for the SW's navigation-fallback cache,
   *  sent with every handoff. Must not read the live DOM: re-handoffs
   *  run after the iframe is mounted. */
  documentHtml: () => string;
  messageChannelFactory?: MessageChannelFactory;
  /** Pre-install wake-capture handle. If provided, the new wake
   *  listener installs first and then this one disposes — no gap
   *  where a wake could fall through. */
  pendingWakes?: EarlyWakeCapture;
}

export interface SwHandoff {
  /** Re-run the handoff. Internally called on wake; exposed for
   *  callers that want to force a fresh port. */
  handOffFreshPort(): Promise<void>;
  dispose(): void;
  /** Test accessor: most-recent `port1`. */
  currentPort(): MessagePortLike | null;
  /** Ask the SW for the outgoing `Cookie` header for `url` (jar ⊕
   *  cookieStore ⊕ `__p2claw_session`). Resolves `""` on no port,
   *  timeout, or empty jar. Used by the WS bridge to cookie the
   *  WebSocket handshake it assembles outside the SW fetch path. */
  getCookieHeader(url: string): Promise<string>;
}

/**
 * Install the SW port bridge plus respawn/replacement listeners.
 *
 * Handoff: wait for `navigator.serviceWorker.controller`, hand the
 * controller a fresh `port2` as `{type: "wrapper_port"}`, keep
 * `port1` wired to `translator`.
 *
 * Re-handoff triggers:
 *   - `p2claw_sw_wake` message — SW respawned without a port.
 *   - `controllerchange` event — a new SW bundle activated and
 *     claimed the page; the fresh module has no port so we re-hand.
 *
 * In-flight requests on the previous port are dropped (surface as
 * fetch failures to the page).
 */
export async function installSwHandoff(
  opts: InstallSwHandoffOptions,
): Promise<SwHandoff> {
  const mkChannel: MessageChannelFactory =
    opts.messageChannelFactory ??
    (() => {
      const c = new MessageChannel();
      return { port1: c.port1, port2: c.port2 };
    });

  let currentPort: MessagePortLike | null = null;
  let disposed = false;

  // Cookie-header request/response over the SW port. The WS bridge
  // assembles the WebSocket handshake in the wrapper (outside the SW
  // fetch path), so it asks the SW — sole owner of the cookie jar +
  // cookieStore snapshot — for the outgoing `Cookie` header. Bounded so
  // a dead/slow port degrades to a bare handshake rather than hanging.
  const COOKIE_HEADER_TIMEOUT_MS = 500;
  let cookieReqSeq = 0;
  const cookiePending = new Map<
    number,
    { resolve: (v: string) => void; timer: ReturnType<typeof setTimeout> }
  >();

  const resolveCookieHeader = (
    msg: Extract<SwMessage, { type: "cookie_header" }>,
  ): void => {
    const entry = cookiePending.get(msg.id);
    if (!entry) return;
    cookiePending.delete(msg.id);
    clearTimeout(entry.timer);
    entry.resolve(msg.cookie);
  };

  const getCookieHeader = (url: string): Promise<string> => {
    const port = currentPort;
    if (!port) return Promise.resolve("");
    const id = ++cookieReqSeq;
    return new Promise<string>((resolve) => {
      const timer = setTimeout(() => {
        if (cookiePending.delete(id)) resolve("");
      }, COOKIE_HEADER_TIMEOUT_MS);
      cookiePending.set(id, { resolve, timer });
      try {
        port.postMessage({
          type: "get_cookie_header",
          v: SW_MSG_VERSION,
          id,
          url,
        } satisfies SwMessage);
      } catch {
        if (cookiePending.delete(id)) {
          clearTimeout(timer);
          resolve("");
        }
      }
    });
  };

  // Install our wake listener first, dispose the pre-install
  // capture second — overlap is fine (handOffFreshPort is
  // idempotent), a gap would drop a wake.
  const onWake = (ev: { data: unknown }): void => {
    const d = ev.data as { type?: string; needs_port?: unknown } | null;
    if (!d || d.type !== "p2claw_sw_wake" || d.needs_port !== true) return;
    // Fire-and-forget — the caller's boot flow has already returned.
    void handOffFreshPort();
  };
  opts.navigatorSw.addEventListener("message", onWake);
  opts.pendingWakes?.dispose();

  await waitForController(opts.navigatorSw);

  async function handOffFreshPort(): Promise<void> {
    const controller = opts.navigatorSw.controller;
    if (!controller) {
      // Lost the controller between wake and re-handoff. Wait once
      // more — the next `controllerchange` will come if the SW is
      // respawning; otherwise the tab is effectively broken and a
      // refresh is needed.
      await waitForController(opts.navigatorSw);
    }
    const c = opts.navigatorSw.controller;
    if (!c) return;

    // Tear down the old port so its `message` listener stops firing
    // if the SW happens to have also kept a reference. Any pending
    // `SwResponseBuilder`s on the old port are orphaned; that's
    // acceptable — they'd never have resolved anyway.
    if (currentPort) {
      try {
        currentPort.close?.();
      } catch {
        /* ignore */
      }
    }

    const { port1, port2 } = mkChannel();
    port1.addEventListener("message", (ev) => {
      const data = ev.data as SwMessage;
      if (data && data.type === "cookie_header") {
        resolveCookieHeader(data);
        return;
      }
      void opts.translator;
      // The wrapper-side fetch handler lives in wrapper.ts; we call
      // into it via the caller-supplied handler. But because the
      // translator is itself in-scope, we route directly here.
      void handleSwMessage(data, opts.translator, port1);
    });
    port1.start?.();
    c.postMessage(
      { type: "wrapper_port", port: port2 },
      [port2 as unknown as Transferable],
    );
    // Push the pristine HTML for the navigation-fallback cache.
    port1.postMessage({
      type: "wrapper_html",
      v: SW_MSG_VERSION,
      body: opts.documentHtml(),
    } satisfies SwMessage);
    currentPort = port1;
  }

  // Initial handoff.
  await handOffFreshPort();

  // Re-handoff on `controllerchange`: a new SW bundle that just
  // claimed the page has no port yet. Ignore transient null
  // transitions (SW unregister); another `controllerchange` lands
  // when the next controller does.
  const onControllerChange = (): void => {
    if (disposed) return;
    if (!opts.navigatorSw.controller) return;
    void handOffFreshPort();
  };
  opts.navigatorSw.addEventListener("controllerchange", onControllerChange);

  return {
    handOffFreshPort,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      opts.navigatorSw.removeEventListener("message", onWake);
      opts.navigatorSw.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
      try {
        currentPort?.close?.();
      } catch {
        /* ignore */
      }
      currentPort = null;
      for (const { resolve, timer } of cookiePending.values()) {
        clearTimeout(timer);
        resolve("");
      }
      cookiePending.clear();
    },
    currentPort: () => currentPort,
    getCookieHeader,
  };
}

/** Tally of `p2claw_sw_wake` events captured before
 *  `installSwHandoff` could install its own listener, plus a
 *  dispose hook. */
export interface EarlyWakeCapture {
  count(): number;
  dispose(): void;
}

/** Minimal `p2claw_sw_wake` tally listener. Install before
 *  `openSession` (which takes hundreds of ms — SW respawns in that
 *  window would otherwise drop on the floor); pass the handle to
 *  `installSwHandoff` via `pendingWakes`. */
export function captureEarlyWakes(
  navigatorSw: SwContainerLike,
): EarlyWakeCapture {
  let n = 0;
  let disposed = false;
  const onMsg = (ev: { data: unknown }): void => {
    const d = ev.data as { type?: string; needs_port?: unknown } | null;
    if (!d || d.type !== "p2claw_sw_wake" || d.needs_port !== true) return;
    n += 1;
  };
  navigatorSw.addEventListener("message", onMsg);
  return {
    count: () => n,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      navigatorSw.removeEventListener("message", onMsg);
    },
  };
}

/** Waits until `navigatorSw.controller` is non-null. */
function waitForController(navigatorSw: SwContainerLike): Promise<void> {
  if (navigatorSw.controller) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const onChange = (): void => {
      if (navigatorSw.controller) {
        navigatorSw.removeEventListener("controllerchange", onChange);
        resolve();
      }
    };
    navigatorSw.addEventListener("controllerchange", onChange);
  });
}

// ---------- wrapper-side fetch bridge ----------
//
// The SW serialises every intercepted fetch as a `fetch` envelope on
// the port; the wrapper side translates that to a translator request
// and streams the response back. Kept here (rather than in
// `wrapper.ts`) so the handoff + response-plumbing live together.

let firstIframeFetchObserved = false;

/**
 * Maximum chunk size (in bytes) we'll postMessage over the SW port.
 * Also the coalescing target: translator chunks (as small as the DC
 * path cap, often 800 B) are accumulated up to this size while more
 * data is immediately available, so the SW sees one message per
 * ~32 KiB instead of one per DC frame. Larger chunks are sliced into
 * pieces before sending — large MessagePort payloads have been seen
 * to silently stall on iPadOS WebKit (no error, no chunk delivery),
 * so 32 KiB keeps every payload comfortably under the threshold.
 */
const MAX_SW_CHUNK_BYTES = 32 * 1024;

/** Bound on the time between body-pump reads making progress. Expiry
 *  surfaces a `fetch_response_error` so the page sees a fetch
 *  failure instead of a blank iframe. */
const BODY_PUMP_STUCK_MS = 30_000;

/** Cadence for the in-flight `getRtcStats()` sampler. Records a
 *  trajectory through any stall so we can tell whether bytes were
 *  arriving and then froze, or never arrived. */
const RTC_STATS_SAMPLE_MS = 5_000;

async function handleSwMessage(
  msg: SwMessage,
  translator: TranslatorClientLike,
  port: MessagePortLike,
): Promise<void> {
  if (msg.type !== "fetch") return;
  const traceFirstIframeFetch =
    !firstIframeFetchObserved && msg.destination === "iframe";
  if (traceFirstIframeFetch) firstIframeFetchObserved = true;
  // Hoisted so the catch path can stop the sampler + watchdog.
  // Default to no-ops; the body-pump block overwrites with the real
  // stoppers.
  let stopRtcStatsSampler: () => void = () => {};
  let stopBodyPumpWatchdog: () => void = () => {};
  try {
    const url = new URL(msg.url);
    if (traceFirstIframeFetch) {
      markBootTiming(
        "iframe_fetch_received",
        `id=${msg.id} path=${url.pathname}${url.search}${formatSwAge(msg.started_epoch_ms)}`,
      );
      markBootTiming("iframe_translator_start", `id=${msg.id}`);
    }
    // Browsers strip `Host` from `Request.headers` (forbidden
    // header); the box's forwarder needs it for routing, so inject
    // from the URL.
    const headers: Array<readonly [string, string]> = [
      ["host", url.host],
      ...msg.headers,
    ];
    const body = msg.body
      ? new ReadableStream<Uint8Array>({
          start(c) {
            c.enqueue(msg.body!);
            c.close();
          },
        })
      : null;
    const res = await translator.request({
      method: msg.method,
      path: url.pathname + url.search,
      headers,
      body,
    });
    if (traceFirstIframeFetch) {
      markBootTiming(
        "iframe_response_head",
        `id=${msg.id} status=${res.status}`,
      );
    }
    port.postMessage({
      type: "fetch_response_head",
      v: SW_MSG_VERSION,
      id: msg.id,
      status: res.status,
      headers: res.headers,
    } satisfies SwMessage);
    const reader = res.body.getReader();
    // Stall watchdog: a single re-arming timeout for the whole pump
    // (NOT one timer per read — a 10 MB download is thousands of
    // reads) checks a per-read progress counter against the deadline;
    // a stalled stream surfaces as a fetch error rather than a hung
    // iframe. Counter totals + per-5s getRtcStats samples are
    // emitted at debug; on stall we replay the latest sample so
    // the trace shows what the WebRTC layer was doing.
    const pumpStartMs = readEpochNowMs();
    let chunkCount = 0;
    let byteCount = 0;
    let stalled = false;
    let readProgress = 0;
    let latestRtcStats: ReturnType<NonNullable<typeof translator.getRtcStats>> extends Promise<infer T> ? T : never =
      null as Awaited<ReturnType<NonNullable<typeof translator.getRtcStats>>>;
    let latestRtcStatsAtMs = 0;
    let samplerStopped = false;
    const stopSampler = (): void => {
      samplerStopped = true;
    };
    stopRtcStatsSampler = stopSampler;
    const sampleLoop = async (): Promise<void> => {
      if (!translator.getRtcStats) return;
      while (!samplerStopped) {
        await new Promise<void>((r) =>
          setTimeout(() => r(), RTC_STATS_SAMPLE_MS),
        );
        if (samplerStopped) return;
        try {
          const s = await translator.getRtcStats();
          const atMs = Math.round(readEpochNowMs() - pumpStartMs);
          if (s === null) {
            debugLog(
              `[p2claw:wrapper] rtc_stats_sample id=${msg.id} at_ms=${atMs} unavailable=true`,
            );
            continue;
          }
          latestRtcStats = s;
          latestRtcStatsAtMs = atMs;
          debugLog(
            `[p2claw:wrapper] rtc_stats_sample id=${msg.id} at_ms=${atMs} dc.messagesReceived=${s.dc.messagesReceived ?? "?"} dc.bytesReceived=${s.dc.bytesReceived ?? "?"} dc.state=${s.dc.state ?? "?"} sctp.bytesReceived=${s.sctp.bytesReceived ?? "?"} sctp.state=${s.sctp.state ?? "?"} transport.iceState=${s.transport.iceState ?? "?"} candidatePair.bytesReceived=${s.candidatePair.bytesReceived ?? "?"}`,
          );
        } catch (e) {
          debugLog(
            `[p2claw:wrapper] rtc_stats_sample id=${msg.id} getStats_failed=${String(e)}`,
          );
        }
      }
    };
    if (translator.getRtcStats) {
      void sampleLoop();
    }
    const onStall = async (): Promise<void> => {
      stalled = true;
      // Attach DC RX summary (if translator exposes it). `orphan_data
      // > 0` means frames arrived for a stream id that didn't match
      // a pending request — the canonical SCTP-reordering symptom.
      const rx = translator.rxSummary?.();
      const rxStr = rx
        ? ` dc_rx={res:${rx.res},data:${rx.data},end:${rx.end},err:${rx.err},ws:${rx.ws_accept + rx.ws_msg + rx.ws_close},other:${rx.other},orphan_res:${rx.orphanRes},orphan_data:${rx.orphanData},bytes:${rx.bytes}}`
        : "";
      console.warn(
        `[p2claw:wrapper] body pump stalled id=${msg.id} chunks=${chunkCount} bytes=${byteCount} after_ms=${BODY_PUMP_STUCK_MS}${rxStr}`,
      );
      // The PC is often already mid-reconnect by the watchdog
      // deadline, so an on-stall `getRtcStats()` would return null.
      // Replay the last non-null sample instead.
      stopSampler();
      if (latestRtcStats !== null) {
        console.warn(
          `[p2claw:wrapper] rtc_stats id=${msg.id} final_at_ms=${latestRtcStatsAtMs} ${JSON.stringify(latestRtcStats)}`,
        );
      } else {
        console.warn(
          `[p2claw:wrapper] rtc_stats id=${msg.id} no_samples_captured (peer null throughout)`,
        );
      }
      port.postMessage({
        type: "fetch_response_error",
        v: SW_MSG_VERSION,
        id: msg.id,
        message: `body pump stalled after ${BODY_PUMP_STUCK_MS}ms (chunks=${chunkCount} bytes=${byteCount})`,
      } satisfies SwMessage);
      // Cancel the reader so the translator can release its
      // pending stream (it'd otherwise keep buffering DATA frames
      // against a consumer that's gone). This also settles the
      // pump's in-flight read, so the loop exits.
      try {
        await reader.cancel("p2claw: body pump watchdog");
      } catch {
        /* best-effort teardown */
      }
    };
    // One timer for the whole pump. On expiry, compare the progress
    // counter against the value seen when the timer was armed — the
    // counter (rather than a clock) makes the deadline immune to
    // wall-clock jumps. Re-arm while reads keep landing.
    let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
    const stopWatchdog = (): void => {
      if (watchdogTimer !== null) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
    };
    stopBodyPumpWatchdog = stopWatchdog;
    const armWatchdog = (): void => {
      const progressAtArm = readProgress;
      watchdogTimer = setTimeout(() => {
        watchdogTimer = null;
        if (readProgress === progressAtArm) {
          void onStall();
        } else {
          armWatchdog();
        }
      }, BODY_PUMP_STUCK_MS);
    };
    armWatchdog();

    // Coalescing buffer for the SW port. Chunks accumulate until
    // MAX_SW_CHUNK_BYTES or until the reader momentarily has nothing
    // pending, then go out as ONE postMessage. Every posted chunk
    // owns its backing buffer exactly (decode allocates fresh;
    // coalesce/split copy), so the buffer rides the transfer list —
    // no structured clone of the payload.
    const bufferedChunks: Uint8Array[] = [];
    let bufferedBytes = 0;
    const postChunk = (chunk: Uint8Array): void => {
      port.postMessage(
        {
          type: "fetch_response_chunk",
          v: SW_MSG_VERSION,
          id: msg.id,
          chunk,
        } satisfies SwMessage,
        [chunk.buffer as ArrayBuffer],
      );
      chunkCount += 1;
      byteCount += chunk.length;
    };
    const flushBuffered = (): void => {
      if (bufferedBytes === 0) return;
      let all: Uint8Array;
      if (bufferedChunks.length === 1) {
        all = bufferedChunks[0]!;
      } else {
        all = new Uint8Array(bufferedBytes);
        let p = 0;
        for (const c of bufferedChunks) {
          all.set(c, p);
          p += c.length;
        }
      }
      bufferedChunks.length = 0;
      bufferedBytes = 0;
      if (all.length <= MAX_SW_CHUNK_BYTES) {
        postChunk(all);
        return;
      }
      // Oversized read: `slice()` (never `subarray`) so each piece
      // owns an exact buffer — posting a subarray view would clone
      // (or transfer) the entire backing buffer.
      for (let offset = 0; offset < all.length; offset += MAX_SW_CHUNK_BYTES) {
        const end = Math.min(offset + MAX_SW_CHUNK_BYTES, all.length);
        postChunk(all.slice(offset, end));
      }
    };

    /** Sentinel for the "is the next read already settled?" race. */
    const READ_PENDING = {};
    let nextRead = reader.read();
    while (true) {
      let result: ReadableStreamReadResult<Uint8Array>;
      if (bufferedBytes > 0) {
        // Peek: if the next read is already settled its reaction wins
        // the race; otherwise flush what we have before parking, so
        // coalescing never adds latency.
        const raced = await Promise.race([
          nextRead,
          Promise.resolve(READ_PENDING),
        ]);
        if (raced === READ_PENDING) {
          if (stalled) break;
          flushBuffered();
          result = await nextRead;
        } else {
          result = raced as ReadableStreamReadResult<Uint8Array>;
        }
      } else {
        result = await nextRead;
      }
      if (stalled || result.done) break;
      const value = result.value;
      nextRead = reader.read();
      readProgress += 1;
      if (value && value.length > 0) {
        bufferedChunks.push(value);
        bufferedBytes += value.length;
        if (bufferedBytes >= MAX_SW_CHUNK_BYTES) flushBuffered();
      }
    }
    if (stalled) return;
    flushBuffered();
    stopWatchdog();
    stopSampler();
    const elapsedMs = readEpochNowMs() - pumpStartMs;
    if (traceFirstIframeFetch) {
      // First iframe nav lands in the boot waterfall at info level.
      markBootTiming(
        "iframe_response_end",
        `id=${msg.id} chunks=${chunkCount} bytes=${byteCount} elapsed_ms=${formatMs(elapsedMs)}`,
      );
    } else {
      debugLog(
        `[p2claw:wrapper] body pump done id=${msg.id} chunks=${chunkCount} bytes=${byteCount} elapsed_ms=${formatMs(elapsedMs)}`,
      );
    }
    port.postMessage({
      type: "fetch_response_end",
      v: SW_MSG_VERSION,
      id: msg.id,
    } satisfies SwMessage);
  } catch (e) {
    // stop the periodic sampler + stall watchdog if the catch path
    // ran while they were active. No-ops when the pump never started.
    stopRtcStatsSampler();
    stopBodyPumpWatchdog();
    if (traceFirstIframeFetch) {
      markBootTiming(
        "iframe_fetch_failed",
        `id=${msg.id} error=${(e as Error).message}`,
      );
    }
    port.postMessage({
      type: "fetch_response_error",
      v: SW_MSG_VERSION,
      id: msg.id,
      message: (e as Error).message,
    } satisfies SwMessage);
  }
}

function markBootTiming(phase: string, detail?: string): void {
  const marker = (
    globalThis as {
      __p2clawMarkBootTiming?: (phase: string, detail?: string) => void;
    }
  ).__p2clawMarkBootTiming;
  if (typeof marker === "function") marker(phase, detail);
}

function formatSwAge(startedEpochMs: number | undefined): string {
  if (typeof startedEpochMs !== "number") return "";
  return ` sw_age=${formatMs(readEpochNowMs() - startedEpochMs)}`;
}

function readEpochNowMs(): number {
  const perf = globalThis.performance;
  if (perf && typeof perf.now === "function") {
    return perf.timeOrigin + perf.now();
  }
  return Date.now();
}

function formatMs(value: number): string {
  return `${(Math.round(value * 10) / 10).toFixed(1)}ms`;
}
